import { makeStyles } from '@material-ui/core/styles'
import { colors, shadows } from 'src/components/app-ui'

const MONO = 'ui-monospace, Menlo, monospace'

const inputBase = {
  width: '100%',
  padding: '12px 14px',
  border: `1px solid ${colors.borderSoft}`,
  borderRadius: '8px',
  outline: 'none',
  background: '#FFFFFF',
  fontFamily: 'inherit',
  fontSize: '15px',
  fontWeight: 400,
  color: colors.navy,
  '&:focus': {
    borderColor: colors.primary,
  },
}

const buttonBase = {
  border: 0,
  borderRadius: '8px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  '&:disabled': {
    cursor: 'default',
  },
}

/** Styles shared by the Integrations views (Automations, Webhooks, API keys). */
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  segmented: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    padding: '3px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
  },
  segment: {
    padding: '7px 14px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.navy,
  },
  segmentActive: {
    background: colors.hover,
    boxShadow: 'inset 0 0 0 1px rgba(0,46,71,.12)',
  },

  // Automations
  automations: {
    maxWidth: 960,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  intro: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  cards: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  card: {
    flex: '1 1 280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '20px 22px',
  },
  wordmark: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '22px',
    fontWeight: 800,
    letterSpacing: '-.03em',
    color: '#201515',
  },
  zapierMark: {
    color: '#FF4F00',
  },
  makeMark: {
    color: '#6D00CC',
  },
  cardDesc: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  connect: {
    alignSelf: 'flex-start',
    padding: '8px 16px',
    border: 0,
    borderRadius: '8px',
    background: colors.primary,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
    '&:hover': {
      background: colors.primaryDark,
    },
  },
  builder: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '22px 24px',
  },
  builderHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    '& $wordmark': {
      fontSize: '20px',
    },
  },

  // Webhooks / API keys pages
  page: {
    maxWidth: 1100,
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  pageHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.navy,
  },
  pageDesc: {
    marginTop: 4,
    fontSize: '14px',
    color: colors.muted,
  },
  addButton: {
    ...buttonBase,
    padding: '11px 20px',
    background: colors.primary,
    fontSize: '14px',
    fontWeight: 600,
    color: '#FFFFFF',
    '&:hover': {
      background: colors.primaryDark,
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px 0',
  },
  // API keys on plans without them.
  locked: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    padding: '48px 24px',
    textAlign: 'center',
  },
  lockedTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: colors.navy,
  },
  lockedLink: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.primary,
    textDecoration: 'none',
  },
  notice: {
    padding: '12px 16px',
    borderRadius: '8px',
    background: '#FFF8EC',
    fontSize: '14px',
    color: colors.warningDark,
  },

  // Tables
  table: {
    overflowX: 'auto',
  },
  tableHead: {
    display: 'grid',
    gap: '16px',
    padding: '14px 24px',
    background: colors.hover,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '14px',
    fontWeight: 500,
    color: colors.muted,
  },
  tableRow: {
    display: 'grid',
    alignItems: 'center',
    gap: '16px',
    padding: '14px 24px',
    borderBottom: `1px solid ${colors.chip}`,
    fontSize: '14px',
    color: colors.navy,
  },
  hoverRow: {
    '&:hover': {
      background: '#F7F7F7',
    },
  },
  alignRight: {
    textAlign: 'right',
  },
  name: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mono: {
    fontFamily: MONO,
    fontSize: '12px',
    color: colors.muted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellMuted: {
    fontSize: '13px',
    color: colors.muted,
  },
  empty: {
    padding: '48px 24px',
    textAlign: 'center',
    fontSize: '14px',
    color: colors.subtle,
  },

  // Dialogs
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'rgba(0,0,0,.48)',
  },
  dialog: {
    width: '100%',
    maxWidth: 440,
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '24px',
    borderRadius: '16px',
    background: '#FFFFFF',
    boxShadow: shadows.popup,
    fontFamily: "'Inter', system-ui, sans-serif",
    color: colors.navy,
  },
  formDialog: {
    maxWidth: 460,
    gap: '16px',
    padding: '24px 28px',
  },
  dialogTitle: {
    fontSize: '18px',
    fontWeight: 600,
  },
  formDialogTitle: {
    fontSize: '20px',
    fontWeight: 600,
  },
  dialogText: {
    fontSize: '14px',
    lineHeight: 1.55,
    color: colors.muted,
  },
  dialogActions: {
    marginTop: 6,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  input: inputBase,
  monoInput: {
    ...inputBase,
    fontFamily: MONO,
    fontSize: '14px',
  },
  selectWrap: {
    position: 'relative',
    display: 'flex',
  },
  select: {
    ...inputBase,
    paddingRight: 36,
    appearance: 'none',
    cursor: 'pointer',
  },
  chevron: {
    position: 'absolute',
    top: '50%',
    right: 12,
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    pointerEvents: 'none',
    color: colors.muted,
  },
  fieldError: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#C9383B',
  },

  // Webhook dialog
  hookDialog: {
    maxWidth: 620,
    gap: '20px',
    padding: '28px 32px',
  },
  hookTitle: {
    fontSize: '22px',
    fontWeight: 600,
  },
  events: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '20px 22px 14px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
  },
  eventsLegend: {
    position: 'absolute',
    top: -9,
    left: 16,
    padding: '0 6px',
    background: '#FFFFFF',
    fontSize: '13px',
    color: colors.muted,
  },
  eventsError: {
    marginTop: -12,
  },
  check: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    fontSize: '15px',
    color: colors.navy,
  },
  checkInput: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
  },
  checkBox: {
    flex: 'none',
    width: 22,
    height: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1.5px solid ${colors.disabled}`,
    borderRadius: '5px',
    background: '#FFFFFF',
    color: '#FFFFFF',
  },
  checkBoxOn: {
    border: `1px solid ${colors.primary}`,
    background: colors.primary,
  },
  switchRow: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '15px',
    color: colors.navy,
  },
  switchTrack: {
    position: 'relative',
    flex: 'none',
    width: 44,
    height: 22,
    borderRadius: '999px',
    background: colors.border,
    transition: 'background 120ms',
  },
  switchKnob: {
    position: 'absolute',
    top: 1,
    left: 1,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(0,0,0,.2)',
    transition: 'left 120ms',
  },
  switchOn: {
    background: 'rgba(1,95,251,.35)',
    '& $switchKnob': {
      left: 23,
      background: colors.primary,
    },
  },
  hookActions: {
    marginTop: 8,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  largeOutline: {
    ...buttonBase,
    padding: '12px 24px',
    border: '1px solid rgba(1,95,251,.5)',
    background: '#FFFFFF',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.primary,
  },
  largePrimary: {
    ...buttonBase,
    padding: '12px 24px',
    background: colors.primary,
    fontSize: '15px',
    fontWeight: 600,
    color: '#FFFFFF',
    '&:hover:not(:disabled)': {
      background: colors.primaryDark,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  iconActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '4px',
  },
  iconButton: {
    display: 'flex',
    padding: 6,
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    color: colors.muted,
    '&:hover': {
      background: colors.hover,
      color: colors.primary,
    },
  },
  iconDanger: {
    '&:hover': {
      background: '#FDE8E8',
      color: colors.error,
    },
  },
  eventChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  eventChip: {
    padding: '2px 8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    background: colors.hover,
    fontSize: '12px',
    color: colors.muted,
    whiteSpace: 'nowrap',
  },

  // API keys
  statusPill: {
    justifySelf: 'start',
    padding: '3px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 500,
  },
  revokeButton: {
    ...buttonBase,
    padding: '4px 6px',
    borderRadius: '6px',
    background: 'transparent',
    fontSize: '13px',
    fontWeight: 500,
    color: '#C9383B',
    '&:hover': {
      background: '#FDECEC',
    },
  },
  keyDialog: {
    maxWidth: 520,
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: '#FFF8EC',
    fontSize: '13px',
    lineHeight: 1.5,
    color: colors.warningDark,
  },
  secret: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 6px 6px 12px',
    borderRadius: '8px',
    background: colors.hover,
  },
  secretText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: '13px',
    color: colors.navy,
    wordBreak: 'break-all',
  },
  copyButton: {
    ...buttonBase,
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '6px',
    background: colors.primary,
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
  },
  keyDone: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  // Small dialog buttons
  textButton: {
    ...buttonBase,
    padding: '9px 14px',
    background: 'transparent',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  primaryButton: {
    ...buttonBase,
    padding: '9px 16px',
    background: colors.primary,
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
    '&:hover:not(:disabled)': {
      background: colors.primaryDark,
    },
    '&:disabled': {
      cursor: 'default',
      background: '#99BFFD',
    },
  },
  outlineButton: {
    ...buttonBase,
    padding: '9px 18px',
    border: '1px solid rgba(1,95,251,.5)',
    background: '#FFFFFF',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.primary,
  },
  dangerButton: {
    ...buttonBase,
    padding: '9px 16px',
    background: colors.error,
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
    '&:hover:not(:disabled)': {
      background: '#C9383B',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
})

export default useStyles
